import { useLazyQuery } from "@apollo/client";
import { PresentationChartLineIcon } from "@heroicons/react/24/outline";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { addDays, addHours, format, startOfDay, startOfHour } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import { ErrorAlert } from "../../components/ErrorAlert/ErrorAlert";
import Loader from "../../components/Loader/Loader";
import Page from "../../components/Page/Page";
import Panel from "../../components/Panel/Panel";
import { OnboardingHistoryData } from "../../graphql/graphql";
import { ONBOARDING_HISTORY } from "../../graphql/queries-gql";
import { CHART_SERIES_COLORS, useChartTheme } from "../../theme/chartTheme";

// Two columns on wide screens, one on narrow ones. `min()` keeps a single
// column from overflowing when the content area is narrower than 520px.
const ChartGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(520px, 100%), 1fr));
  gap: 16px;
  align-items: start;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

enum DateRange {
  Last24Hours,
  Last7Days,
  Last14Days,
  Last30Days,
  Last90Days,
  Custom,
}

const [BLUE, GREEN, ORANGE, PINK, PURPLE, CYAN, RED, LIME, INDIGO, YELLOW] =
  CHART_SERIES_COLORS;

const datePickerSlotProps = {
  textField: {
    size: "small" as const,
    sx: { width: 160 },
  },
};

const formatDateLabel = (dateStr: string, hourly: boolean) => {
  try {
    if (hourly) {
      return format(new Date(dateStr), "MMM d HH:mm");
    }
    // For daily view, format using UTC date to avoid timezone shift
    const d = new Date(dateStr);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[d.getUTCMonth()]} ${d.getUTCDate()}`;
  } catch {
    return dateStr;
  }
};

const AveragesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${(props) => props.theme.border};
`;

const AverageItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const AverageValue = styled.strong`
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${(props) => props.theme.textColor};
`;

const AverageDot = styled.span<{ $color: string; $faded?: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
  opacity: ${(props) => (props.$faded ? 0.45 : 1)};
  display: inline-block;
  flex-shrink: 0;
`;

// Averages read as "Avg hot likes IAP", so only the first letter is lowered
const lowerFirst = (label: string) =>
  label.charAt(0).toLowerCase() + label.slice(1);

const COMPARISON_DESCRIPTION = "Compared with the previous 24 hours";

const ChartWrapper: React.FC<{
  title: string;
  description?: string;
  children: React.ReactNode;
  averages?: Array<{
    label: string;
    value: string;
    color: string;
    faded?: boolean;
  }>;
}> = ({ title, description, children, averages }) => (
  <Panel title={title} description={description}>
    <ResponsiveContainer width="100%" height={300} debounce={100}>
      {children as React.ReactElement}
    </ResponsiveContainer>
    {averages && averages.length > 0 && (
      <AveragesContainer>
        {averages.map((avg) => (
          <AverageItem key={avg.label}>
            <AverageDot $color={avg.color} $faded={avg.faded} />
            <span>
              Avg {avg.label}: <AverageValue>{avg.value}</AverageValue>
            </span>
          </AverageItem>
        ))}
      </AveragesContainer>
    )}
  </Panel>
);

const calcAvg = (
  data: Array<Record<string, unknown>>,
  key: string,
  isPercent = false,
): string => {
  if (data.length === 0) return "0";
  const sum = data.reduce((acc, d) => acc + (Number(d[key]) || 0), 0);
  const avg = sum / data.length;
  return isPercent ? `${avg.toFixed(2)}%` : avg.toFixed(1);
};

const OnboardingAnalytics = () => {
  const [fromDate, setFromDate] = useState<Date>(
    startOfDay(addDays(new Date(), -30)),
  );
  const [toDate, setToDate] = useState<Date>(new Date());
  const [platform, setPlatform] = useState<string | undefined>('ios');
  const [dateRange, setDateRange] = useState<DateRange>(DateRange.Last30Days);
  const chart = useChartTheme();

  const isHourly = dateRange === DateRange.Last24Hours;

  const [fetchHistory, { data, loading, error }] = useLazyQuery<{
    onboardingHistory: OnboardingHistoryData;
  }>(ONBOARDING_HISTORY, {
    fetchPolicy: "network-only",
    variables: {
      args: { fromDate, toDate, platform },
    },
  });

  const prevFromDate = useMemo(() => addHours(fromDate, -24), [fromDate]);
  const prevToDate = useMemo(() => addHours(toDate, -24), [toDate]);

  const [fetchPrevHistory, { data: prevData, loading: prevLoading }] = useLazyQuery<{
    onboardingHistory: OnboardingHistoryData;
  }>(ONBOARDING_HISTORY, {
    fetchPolicy: "network-only",
    variables: {
      args: { fromDate: prevFromDate, toDate: prevToDate, platform },
    },
  });

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, platform]);

  useEffect(() => {
    if (isHourly) {
      fetchPrevHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHourly, prevFromDate, prevToDate, platform]);

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    const now = new Date();
    switch (range) {
      case DateRange.Last24Hours:
        setFromDate(addHours(now, -24));
        setToDate(now);
        break;
      case DateRange.Last7Days:
        setFromDate(startOfDay(addDays(now, -7)));
        setToDate(now);
        break;
      case DateRange.Last14Days:
        setFromDate(startOfDay(addDays(now, -14)));
        setToDate(now);
        break;
      case DateRange.Last30Days:
        setFromDate(startOfDay(addDays(now, -30)));
        setToDate(now);
        break;
      case DateRange.Last90Days:
        setFromDate(startOfDay(addDays(now, -90)));
        setToDate(now);
        break;
      case DateRange.Custom:
        break;
    }
  };

  const chartData = useMemo(() => {
    if (!data?.onboardingHistory?.onboardingData) return [];

    // Aggregate by day (or hour for 24h view), summing iOS + Android
    const byTimestamp = new Map<
      string,
      {
        date: string;
        totalUsers: number;
        totalPremiumUsers: number;
        totalIapUsers: number;
        newPremium1Week: number;
        renewalPremium1Week: number;
        newPremium3Months: number;
        renewalPremium3Months: number;
        totalHotLikesIap: number;
        totalWinksIap: number;
        totalUnlockChatIap: number;
        totalBoostIap: number;
        totalSpotlightIap: number;
        totalVotesIap: number;
        totalLiveGiftIap: number;
        totalFinishedOnboarding: number;
        femaleFinishedOnboarding: number;
        photoFinishedOnboarding: number;
        pushFinishedOnboarding: number;
        totalPremiumOnboarding: number;
        totalPushActivated: number;
        totalRewardedAdsWatched: number;
        subscriptionSources: Map<string, number>;
        appVersions: Map<string, number>;
        inviteCodesUsed: number;
        totalActiveInviteCodes: number;
        userCount: number;
      }
    >();

    // First pass: aggregate by exact hour (adds iOS + Android)
    const byHour = new Map<
      string,
      typeof byTimestamp extends Map<string, infer V> ? V : never
    >();

    for (const d of data.onboardingHistory.onboardingData) {
      const hourKey = startOfHour(new Date(d.date)).toISOString();
      const existing = byHour.get(hourKey);
      if (existing) {
        existing.totalUsers += d.totalUsers;
        existing.totalPremiumUsers += d.totalPremiumUsers;
        existing.totalIapUsers += d.totalIapUsers;
        existing.newPremium1Week += d.newPremium1Week;
        existing.renewalPremium1Week += d.renewalPremium1Week;
        existing.newPremium3Months += d.newPremium3Months;
        existing.renewalPremium3Months += d.renewalPremium3Months;
        existing.totalHotLikesIap += d.totalHotLikesIap;
        existing.totalWinksIap += d.totalWinksIap;
        existing.totalUnlockChatIap += d.totalUnlockChatIap;
        existing.totalBoostIap += d.totalBoostIap;
        existing.totalSpotlightIap += d.totalSpotlightIap;
        existing.totalVotesIap += d.totalVotesIap;
        existing.totalLiveGiftIap += d.totalLiveGiftIap;
        existing.totalFinishedOnboarding += d.totalFinishedOnboarding;
        existing.femaleFinishedOnboarding += d.femaleFinishedOnboarding;
        existing.photoFinishedOnboarding += d.photoFinishedOnboarding;
        existing.pushFinishedOnboarding += d.pushFinishedOnboarding;
        existing.totalPremiumOnboarding += d.totalPremiumOnboarding;
        existing.totalPushActivated += d.totalPushActivated;
        existing.totalRewardedAdsWatched += d.totalRewardedAdsWatched;
        existing.inviteCodesUsed += (d as any).inviteCodesUsed || 0;
        existing.totalActiveInviteCodes += (d as any).totalActiveInviteCodes || 0;
        existing.userCount += d.userCount;
        d.subscriptionSources.forEach((s) => {
          existing.subscriptionSources.set(
            s.source,
            (existing.subscriptionSources.get(s.source) || 0) + s.count,
          );
        });
        d.appVersions.forEach((v) => {
          existing.appVersions.set(
            v.version,
            (existing.appVersions.get(v.version) || 0) + v.count,
          );
        });
      } else {
        const sourcesMap = new Map<string, number>();
        d.subscriptionSources.forEach((s) => sourcesMap.set(s.source, s.count));
        const versionsMap = new Map<string, number>();
        d.appVersions.forEach((v) => versionsMap.set(v.version, v.count));
        byHour.set(hourKey, {
          date: hourKey,
          totalUsers: d.totalUsers,
          totalPremiumUsers: d.totalPremiumUsers,
          totalIapUsers: d.totalIapUsers,
          newPremium1Week: d.newPremium1Week,
          renewalPremium1Week: d.renewalPremium1Week,
          newPremium3Months: d.newPremium3Months,
          renewalPremium3Months: d.renewalPremium3Months,
          totalHotLikesIap: d.totalHotLikesIap,
          totalWinksIap: d.totalWinksIap,
          totalUnlockChatIap: d.totalUnlockChatIap,
          totalBoostIap: d.totalBoostIap,
          totalSpotlightIap: d.totalSpotlightIap,
          totalVotesIap: d.totalVotesIap,
          totalLiveGiftIap: d.totalLiveGiftIap,
          totalFinishedOnboarding: d.totalFinishedOnboarding,
          femaleFinishedOnboarding: d.femaleFinishedOnboarding,
          photoFinishedOnboarding: d.photoFinishedOnboarding,
          pushFinishedOnboarding: d.pushFinishedOnboarding,
          totalPremiumOnboarding: d.totalPremiumOnboarding,
          totalPushActivated: d.totalPushActivated,
          totalRewardedAdsWatched: d.totalRewardedAdsWatched,
          inviteCodesUsed: (d as any).inviteCodesUsed || 0,
          totalActiveInviteCodes: (d as any).totalActiveInviteCodes || 0,
          userCount: d.userCount,
          subscriptionSources: sourcesMap,
          appVersions: versionsMap,
        });
      }
    }

    // Second pass: if daily view, aggregate hours into days
    if (isHourly) {
      byHour.forEach((val, key) => {
        byTimestamp.set(key, val);
      });
    } else {
      byHour.forEach((entry) => {
        // Group by UTC day to match backend date boundaries
        const d = new Date(entry.date);
        const dayKey = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString();
        const existing = byTimestamp.get(dayKey);
        if (existing) {
          existing.totalUsers += entry.totalUsers;
          existing.totalPremiumUsers += entry.totalPremiumUsers;
          existing.totalIapUsers += entry.totalIapUsers;
          existing.newPremium1Week += entry.newPremium1Week;
          existing.renewalPremium1Week += entry.renewalPremium1Week;
          existing.newPremium3Months += entry.newPremium3Months;
          existing.renewalPremium3Months += entry.renewalPremium3Months;
          existing.totalHotLikesIap += entry.totalHotLikesIap;
          existing.totalWinksIap += entry.totalWinksIap;
          existing.totalUnlockChatIap += entry.totalUnlockChatIap;
          existing.totalBoostIap += entry.totalBoostIap;
          existing.totalSpotlightIap += entry.totalSpotlightIap;
          existing.totalVotesIap += entry.totalVotesIap;
          existing.totalLiveGiftIap += entry.totalLiveGiftIap;
          existing.totalFinishedOnboarding += entry.totalFinishedOnboarding;
          existing.femaleFinishedOnboarding += entry.femaleFinishedOnboarding;
          existing.photoFinishedOnboarding += entry.photoFinishedOnboarding;
          existing.pushFinishedOnboarding += entry.pushFinishedOnboarding;
          existing.totalPremiumOnboarding += entry.totalPremiumOnboarding;
          existing.totalPushActivated += entry.totalPushActivated;
          existing.totalRewardedAdsWatched += entry.totalRewardedAdsWatched;
          existing.inviteCodesUsed += entry.inviteCodesUsed;
          existing.totalActiveInviteCodes = Math.max(existing.totalActiveInviteCodes, entry.totalActiveInviteCodes);
          // userCount: take the max (latest snapshot for the day)
          existing.userCount = Math.max(existing.userCount, entry.userCount);
          entry.subscriptionSources.forEach((count: number, source: string) => {
            existing.subscriptionSources.set(
              source,
              (existing.subscriptionSources.get(source) || 0) + count,
            );
          });
          entry.appVersions.forEach((count: number, version: string) => {
            existing.appVersions.set(
              version,
              (existing.appVersions.get(version) || 0) + count,
            );
          });
        } else {
          byTimestamp.set(dayKey, { ...entry, date: dayKey });
        }
      });
    }

    return Array.from(byTimestamp.values())
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map((d) => ({
        dateLabel: formatDateLabel(d.date, isHourly),
        userCount: d.userCount,
        totalUsers: d.totalUsers,
        totalPremiumUsers: d.totalPremiumUsers,
        totalIapUsers: d.totalIapUsers,
        newPremium1Week: d.newPremium1Week,
        renewalPremium1Week: d.renewalPremium1Week,
        newPremium3Months: d.newPremium3Months,
        renewalPremium3Months: d.renewalPremium3Months,
        totalHotLikesIap: d.totalHotLikesIap,
        totalWinksIap: d.totalWinksIap,
        totalUnlockChatIap: d.totalUnlockChatIap,
        totalBoostIap: d.totalBoostIap,
        totalSpotlightIap: d.totalSpotlightIap,
        totalVotesIap: d.totalVotesIap,
        totalLiveGiftIap: d.totalLiveGiftIap,
        totalFinishedOnboarding: d.totalFinishedOnboarding,
        femaleFinishedOnboarding: d.femaleFinishedOnboarding,
        photoFinishedOnboarding: d.photoFinishedOnboarding,
        pushFinishedOnboarding: d.pushFinishedOnboarding,
        totalPremiumOnboarding: d.totalPremiumOnboarding,
        totalPushActivated: d.totalPushActivated,
        totalRewardedAdsWatched: d.totalRewardedAdsWatched,
        inviteCodesUsed: d.inviteCodesUsed,
        totalActiveInviteCodes: d.totalActiveInviteCodes,
        paidConversionRate:
          d.totalUsers > 0
            ? Number(((d.totalPremiumUsers / d.totalUsers) * 100).toFixed(2))
            : 0,
        photoOnboardingRate:
          d.totalFinishedOnboarding > 0
            ? Number(
                (
                  (d.photoFinishedOnboarding / d.totalFinishedOnboarding) *
                  100
                ).toFixed(2),
              )
            : 0,
        pushOnboardingRate:
          d.totalFinishedOnboarding > 0
            ? Number(
                (
                  (d.pushFinishedOnboarding / d.totalFinishedOnboarding) *
                  100
                ).toFixed(2),
              )
            : 0,
        femaleOnboardingRate:
          d.totalFinishedOnboarding > 0
            ? Number(
                (
                  (d.femaleFinishedOnboarding / d.totalFinishedOnboarding) *
                  100
                ).toFixed(2),
              )
            : 0,
        premiumOfOnboardingRate:
          d.totalFinishedOnboarding > 0
            ? Number(
                (
                  (d.totalPremiumUsers / d.totalFinishedOnboarding) *
                  100
                ).toFixed(2),
              )
            : 0,
        onboardingCompletionRate:
          d.totalUsers > 0
            ? Number(
                ((d.totalFinishedOnboarding / d.totalUsers) * 100).toFixed(2),
              )
            : 0,
        onboardingPaywallRate:
          d.totalUsers > 0
            ? Number(
                ((d.totalPremiumOnboarding / d.totalUsers) * 100).toFixed(2),
              )
            : 0,
        subscriptionSources: Array.from(d.subscriptionSources.entries()).map(
          ([source, count]) => ({ source, count }),
        ),
        appVersions: Array.from(d.appVersions.entries()).map(
          ([version, count]) => ({ version, count }),
        ),
      }));
  }, [data, isHourly]);

  // Build previous 24h data keyed by hour (0-23) for comparison overlay
  const prevHourlyTotals = useMemo(() => {
    if (!isHourly || !prevData?.onboardingHistory?.onboardingData) return new Map<number, number>();
    const byHour = new Map<number, number>();
    for (const d of prevData.onboardingHistory.onboardingData) {
      const hour = new Date(d.date).getHours();
      byHour.set(hour, (byHour.get(hour) || 0) + d.totalUsers);
    }
    return byHour;
  }, [isHourly, prevData]);

  // Build previous 24h premium data keyed by hour (0-23)
  const prevHourlyPremium = useMemo(() => {
    if (!isHourly || !prevData?.onboardingHistory?.onboardingData) return new Map<number, number>();
    const byHour = new Map<number, number>();
    for (const d of prevData.onboardingHistory.onboardingData) {
      const hour = new Date(d.date).getHours();
      byHour.set(hour, (byHour.get(hour) || 0) + d.totalPremiumUsers);
    }
    return byHour;
  }, [isHourly, prevData]);

  // Merge previous 24h "totalUsers" into chartData for the New Users chart
  const newUsersChartData = useMemo(() => {
    if (!isHourly || prevHourlyTotals.size === 0) return chartData;
    return chartData.map((d) => {
      const hour = parseInt(d.dateLabel.split(' ')[2]?.split(':')[0] ?? '', 10);
      return {
        ...d,
        prevTotalUsers: isNaN(hour) ? undefined : (prevHourlyTotals.get(hour) ?? 0),
      };
    });
  }, [chartData, isHourly, prevHourlyTotals]);

  // Merge previous 24h premium into chartData for the New Premium Users chart
  const premiumChartData = useMemo(() => {
    if (!isHourly || prevHourlyPremium.size === 0) return chartData;
    return chartData.map((d) => {
      const hour = parseInt(d.dateLabel.split(' ')[2]?.split(':')[0] ?? '', 10);
      return {
        ...d,
        prevTotalPremiumUsers: isNaN(hour) ? undefined : (prevHourlyPremium.get(hour) ?? 0),
      };
    });
  }, [chartData, isHourly, prevHourlyPremium]);

  // Build previous 24h IAP data keyed by hour (0-23)
  const prevHourlyIap = useMemo(() => {
    if (!isHourly || !prevData?.onboardingHistory?.onboardingData) return new Map<number, number>();
    const byHour = new Map<number, number>();
    for (const d of prevData.onboardingHistory.onboardingData) {
      const hour = new Date(d.date).getHours();
      byHour.set(hour, (byHour.get(hour) || 0) + d.totalIapUsers);
    }
    return byHour;
  }, [isHourly, prevData]);

  // Merge previous 24h IAP into chartData for the New IAP chart
  const iapChartData = useMemo(() => {
    if (!isHourly || prevHourlyIap.size === 0) return chartData;
    return chartData.map((d) => {
      const hour = parseInt(d.dateLabel.split(' ')[2]?.split(':')[0] ?? '', 10);
      return {
        ...d,
        prevTotalIapUsers: isNaN(hour) ? undefined : (prevHourlyIap.get(hour) ?? 0),
      };
    });
  }, [chartData, isHourly, prevHourlyIap]);

  // Build previous 24h push activated data keyed by hour (0-23)
  const prevHourlyPush = useMemo(() => {
    if (!isHourly || !prevData?.onboardingHistory?.onboardingData) return new Map<number, number>();
    const byHour = new Map<number, number>();
    for (const d of prevData.onboardingHistory.onboardingData) {
      const hour = new Date(d.date).getHours();
      byHour.set(hour, (byHour.get(hour) || 0) + d.totalPushActivated);
    }
    return byHour;
  }, [isHourly, prevData]);

  // Merge previous 24h push into chartData for the New Push Activated chart
  const pushChartData = useMemo(() => {
    if (!isHourly || prevHourlyPush.size === 0) return chartData;
    return chartData.map((d) => {
      const hour = parseInt(d.dateLabel.split(' ')[2]?.split(':')[0] ?? '', 10);
      return {
        ...d,
        prevTotalPushActivated: isNaN(hour) ? undefined : (prevHourlyPush.get(hour) ?? 0),
      };
    });
  }, [chartData, isHourly, prevHourlyPush]);

  // Build previous 24h data for all revenue-event-based keys at once
  const prevHourlyRevenue = useMemo(() => {
    const keys = [
      'newPremium1Week',
      'renewalPremium1Week',
      'newPremium3Months',
      'renewalPremium3Months',
      'totalHotLikesIap',
      'totalWinksIap',
      'totalUnlockChatIap',
      'totalBoostIap',
      'totalSpotlightIap',
      'totalVotesIap',
      'totalLiveGiftIap',
    ];
    const result = new Map<string, Map<number, number>>();
    if (!isHourly || !prevData?.onboardingHistory?.onboardingData) return result;
    for (const key of keys) {
      const byHour = new Map<number, number>();
      for (const d of prevData.onboardingHistory.onboardingData) {
        const hour = new Date(d.date).getHours();
        byHour.set(hour, (byHour.get(hour) || 0) + (d as any)[key]);
      }
      result.set(key, byHour);
    }
    return result;
  }, [isHourly, prevData]);

  // Helper: get chart data with previous 24h overlay for a given key
  const getRevenueChartData = (key: string) => {
    const prevMap = prevHourlyRevenue.get(key);
    if (!isHourly || !prevMap || prevMap.size === 0) return chartData;
    return chartData.map((d) => {
      const hour = parseInt(d.dateLabel.split(' ')[2]?.split(':')[0] ?? '', 10);
      return {
        ...d,
        [`prev_${key}`]: isNaN(hour) ? undefined : (prevMap.get(hour) ?? 0),
      };
    });
  };

  // Shared line styles: solid for the metric itself, dashed for a rate on the
  // right axis and for the previous 24h comparison.
  const primaryLineProps = {
    strokeWidth: 2,
    dot: { r: 3 },
    activeDot: { r: 5 },
  };
  const rateLineProps = {
    strokeWidth: 2,
    strokeDasharray: "5 5",
    dot: { r: 2 },
    activeDot: { r: 4 },
  };
  const comparisonLineProps = rateLineProps;

  // Helper: render a chart with optional previous 24h comparison
  const renderRevenueChart = (
    title: string,
    dataKey: string,
    color: string,
  ) => {
    const prevMap = prevHourlyRevenue.get(dataKey);
    const hasPrev = isHourly && !prevLoading && prevMap && prevMap.size > 0;
    const data = getRevenueChartData(dataKey);
    const prevKey = `prev_${dataKey}`;

    return (
      <ChartWrapper
        title={title}
        description={hasPrev ? COMPARISON_DESCRIPTION : undefined}
        averages={[
          { label: lowerFirst(title), value: calcAvg(chartData, dataKey), color },
          ...(hasPrev
            ? [{ label: 'prev 24h', value: calcAvg(data, prevKey), color: chart.mutedSeries }]
            : []),
        ]}
      >
        <LineChart data={data}>
          <CartesianGrid {...chart.grid} />
          <XAxis dataKey="dateLabel" {...chart.axis} />
          <YAxis {...chart.axis} />
          <Tooltip {...chart.tooltip} />
          {hasPrev && <Legend {...chart.legend} />}
          <Line
            type="monotone"
            dataKey={dataKey}
            name={title}
            stroke={color}
            {...primaryLineProps}
          />
          {hasPrev && (
            <Line
              type="monotone"
              dataKey={prevKey}
              name="Previous 24h"
              stroke={chart.mutedSeries}
              {...comparisonLineProps}
            />
          )}
        </LineChart>
      </ChartWrapper>
    );
  };

  // Helper: render a subscription chart with new + renewal lines and optional previous 24h
  const renderSubscriptionChart = (
    title: string,
    newKey: string,
    renewalKey: string,
    newColor: string,
    renewalColor: string,
  ) => {
    const prevNewMap = prevHourlyRevenue.get(newKey);
    const prevRenewalMap = prevHourlyRevenue.get(renewalKey);
    const hasPrevNew = isHourly && !prevLoading && prevNewMap && prevNewMap.size > 0;
    const hasPrevRenewal = isHourly && !prevLoading && prevRenewalMap && prevRenewalMap.size > 0;
    const hasPrev = hasPrevNew || hasPrevRenewal;
    const data = chartData.map((d) => {
      const hour = parseInt(d.dateLabel.split(' ')[2]?.split(':')[0] ?? '', 10);
      return {
        ...d,
        ...(hasPrevNew ? { [`prev_${newKey}`]: isNaN(hour) ? undefined : (prevNewMap!.get(hour) ?? 0) } : {}),
        ...(hasPrevRenewal ? { [`prev_${renewalKey}`]: isNaN(hour) ? undefined : (prevRenewalMap!.get(hour) ?? 0) } : {}),
      };
    });

    // Two comparison lines here, so each keeps a faded version of its own
    // series color instead of the single muted comparison color.
    return (
      <ChartWrapper
        title={title}
        description={hasPrev ? COMPARISON_DESCRIPTION : undefined}
        averages={[
          { label: 'new', value: calcAvg(chartData, newKey), color: newColor },
          { label: 'renewal', value: calcAvg(chartData, renewalKey), color: renewalColor },
          ...(hasPrevNew ? [{ label: 'prev new', value: calcAvg(data, `prev_${newKey}`), color: newColor, faded: true }] : []),
          ...(hasPrevRenewal ? [{ label: 'prev renewal', value: calcAvg(data, `prev_${renewalKey}`), color: renewalColor, faded: true }] : []),
        ]}
      >
        <LineChart data={data}>
          <CartesianGrid {...chart.grid} />
          <XAxis dataKey="dateLabel" {...chart.axis} />
          <YAxis {...chart.axis} />
          <Tooltip {...chart.tooltip} />
          <Legend {...chart.legend} />
          <Line
            type="monotone"
            dataKey={newKey}
            name="New"
            stroke={newColor}
            {...primaryLineProps}
          />
          <Line
            type="monotone"
            dataKey={renewalKey}
            name="Renewal"
            stroke={renewalColor}
            {...primaryLineProps}
          />
          {hasPrevNew && (
            <Line
              type="monotone"
              dataKey={`prev_${newKey}`}
              name="Prev new"
              stroke={newColor}
              strokeOpacity={0.45}
              {...comparisonLineProps}
            />
          )}
          {hasPrevRenewal && (
            <Line
              type="monotone"
              dataKey={`prev_${renewalKey}`}
              name="Prev renewal"
              stroke={renewalColor}
              strokeOpacity={0.45}
              {...comparisonLineProps}
            />
          )}
        </LineChart>
      </ChartWrapper>
    );
  };

  // Helper: count on the left axis plus its share (%) on the right axis
  const renderCountAndRateChart = (
    title: string,
    countKey: string,
    countName: string,
    countColor: string,
    rateKey: string,
    rateName: string,
    rateColor: string,
  ) => (
    <ChartWrapper
      title={title}
      averages={[
        {
          label: lowerFirst(countName),
          value: calcAvg(chartData, countKey),
          color: countColor,
        },
        {
          label: lowerFirst(rateName),
          value: calcAvg(chartData, rateKey, true),
          color: rateColor,
        },
      ]}
    >
      <LineChart data={chartData}>
        <CartesianGrid {...chart.grid} />
        <XAxis dataKey="dateLabel" {...chart.axis} />
        <YAxis yAxisId="left" {...chart.axis} />
        <YAxis
          yAxisId="right"
          orientation="right"
          {...chart.axis}
          tickFormatter={(v) => `${v}%`}
        />
        <Tooltip
          {...chart.tooltip}
          formatter={(value: number, name: string) =>
            name === rateName ? [`${value}%`, name] : [value, name]
          }
        />
        <Legend {...chart.legend} />
        <Line
          yAxisId="left"
          type="monotone"
          dataKey={countKey}
          name={countName}
          stroke={countColor}
          {...primaryLineProps}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey={rateKey}
          name={rateName}
          stroke={rateColor}
          {...rateLineProps}
        />
      </LineChart>
    </ChartWrapper>
  );

  const topSources = useMemo(() => {
    const totals = new Map<string, number>();
    chartData.forEach((d) => {
      d.subscriptionSources.forEach((s) => {
        totals.set(s.source, (totals.get(s.source) || 0) + s.count);
      });
    });
    return Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([source]) => source);
  }, [chartData]);

  const subscriptionSourceData = useMemo(() => {
    const topSet = new Set(topSources);
    return chartData.map((d) => {
      const entry: Record<string, string | number> = {
        dateLabel: d.dateLabel,
      };
      let otherCount = 0;
      topSources.forEach((source) => {
        const found = d.subscriptionSources.find((s) => s.source === source);
        entry[source] = found ? found.count : 0;
      });
      d.subscriptionSources.forEach((s) => {
        if (!topSet.has(s.source)) {
          otherCount += s.count;
        }
      });
      entry['Other'] = otherCount;
      return entry;
    });
  }, [chartData, topSources]);

  const topVersions = useMemo(() => {
    const versionSet = new Set<string>();
    chartData.forEach((d) => {
      d.appVersions.forEach((v) => versionSet.add(v.version));
    });
    // Sort descending by version name (higher numbers = newer), take top 5
    return Array.from(versionSet)
      .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
      .slice(0, 5);
  }, [chartData]);

  const appVersionChartData = useMemo(() => {
    const topSet = new Set(topVersions);
    return chartData.map((d) => {
      const entry: Record<string, string | number> = {
        dateLabel: d.dateLabel,
      };
      let otherCount = 0;
      topVersions.forEach((version) => {
        const found = d.appVersions.find((v) => v.version === version);
        entry[version] = found ? found.count : 0;
      });
      d.appVersions.forEach((v) => {
        if (!topSet.has(v.version)) {
          otherCount += v.count;
        }
      });
      entry['Other'] = otherCount;
      return entry;
    });
  }, [chartData, topVersions]);

  const renderSingleLineChart = (
    title: string,
    dataKey: string,
    color: string,
  ) => (
    <ChartWrapper
      title={title}
      averages={[{ label: lowerFirst(title), value: calcAvg(chartData, dataKey), color }]}
    >
      <LineChart data={chartData}>
        <CartesianGrid {...chart.grid} />
        <XAxis dataKey="dateLabel" {...chart.axis} />
        <YAxis {...chart.axis} />
        <Tooltip {...chart.tooltip} />
        <Line
          type="monotone"
          dataKey={dataKey}
          name={title}
          stroke={color}
          {...primaryLineProps}
        />
      </LineChart>
    </ChartWrapper>
  );

  const hasPrevTotals = isHourly && prevHourlyTotals.size > 0;
  const hasPrevPremium = isHourly && prevHourlyPremium.size > 0;
  const hasPrevIap = isHourly && prevHourlyIap.size > 0;
  const hasPrevPush = isHourly && prevHourlyPush.size > 0;

  return (
    <Page
      title="Onboarding analytics"
      description="Sign-ups, onboarding, premium and in-app purchase trends. The 24 hours view adds the previous 24 hours for comparison."
      actions={
        <ToggleButtonGroup
          value={platform}
          exclusive
          onChange={(_, value) => setPlatform(value)}
          size="small"
        >
          <ToggleButton value="ios">iOS</ToggleButton>
          <ToggleButton value="android">Android</ToggleButton>
        </ToggleButtonGroup>
      }
      toolbar={
        <>
          <ToggleButtonGroup
            value={dateRange}
            exclusive
            onChange={(_, value) =>
              value !== null && handleDateRangeChange(value)
            }
            size="small"
          >
            <ToggleButton value={DateRange.Last24Hours}>24 hours</ToggleButton>
            <ToggleButton value={DateRange.Last7Days}>7 days</ToggleButton>
            <ToggleButton value={DateRange.Last14Days}>14 days</ToggleButton>
            <ToggleButton value={DateRange.Last30Days}>30 days</ToggleButton>
            <ToggleButton value={DateRange.Last90Days}>90 days</ToggleButton>
          </ToggleButtonGroup>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {dateRange === DateRange.Last24Hours ? (
              <DatePicker
                label="Date"
                value={fromDate}
                onChange={(date) => {
                  if (date) {
                    setFromDate(startOfDay(date));
                    setToDate(addHours(startOfDay(date), 24));
                  }
                }}
                disableFuture
                slotProps={datePickerSlotProps}
              />
            ) : (
              <>
                <DatePicker
                  label="From"
                  value={fromDate}
                  onChange={(date) => {
                    if (date) {
                      setDateRange(DateRange.Custom);
                      setFromDate(startOfDay(date));
                    }
                  }}
                  disableFuture
                  slotProps={datePickerSlotProps}
                />
                <DatePicker
                  label="To"
                  value={toDate}
                  onChange={(date) => {
                    if (date) {
                      setDateRange(DateRange.Custom);
                      setToDate(date);
                    }
                  }}
                  disableFuture
                  slotProps={datePickerSlotProps}
                />
              </>
            )}
          </LocalizationProvider>
        </>
      }
    >
      {error && <ErrorAlert error={error} />}

      {loading ? (
        <LoadingContainer>
          <Loader />
        </LoadingContainer>
      ) : chartData.length > 0 ? (
        <ChartGrid>
          {renderSingleLineChart("Total user count", "userCount", PURPLE)}
          <ChartWrapper
            title="New users"
            description={hasPrevTotals && !prevLoading ? COMPARISON_DESCRIPTION : undefined}
            averages={[
              { label: "new users", value: calcAvg(chartData, "totalUsers"), color: GREEN },
              ...(hasPrevTotals
                ? [{ label: "prev 24h", value: calcAvg(newUsersChartData, "prevTotalUsers"), color: chart.mutedSeries }]
                : []),
            ]}
          >
            <LineChart data={newUsersChartData}>
              <CartesianGrid {...chart.grid} />
              <XAxis dataKey="dateLabel" {...chart.axis} />
              <YAxis {...chart.axis} />
              <Tooltip {...chart.tooltip} />
              {hasPrevTotals && <Legend {...chart.legend} />}
              <Line
                type="monotone"
                dataKey="totalUsers"
                name="New users"
                stroke={GREEN}
                {...primaryLineProps}
              />
              {hasPrevTotals && (
                <Line
                  type="monotone"
                  dataKey="prevTotalUsers"
                  name="Previous 24h"
                  stroke={chart.mutedSeries}
                  {...comparisonLineProps}
                />
              )}
            </LineChart>
          </ChartWrapper>
          <ChartWrapper
            title="New premium users"
            description={hasPrevPremium && !prevLoading ? COMPARISON_DESCRIPTION : undefined}
            averages={[
              {
                label: "premium users",
                value: calcAvg(chartData, "totalPremiumUsers"),
                color: YELLOW,
              },
              {
                label: "premium %",
                value: calcAvg(chartData, "paidConversionRate", true),
                color: ORANGE,
              },
              ...(hasPrevPremium
                ? [{ label: "prev 24h", value: calcAvg(premiumChartData, "prevTotalPremiumUsers"), color: chart.mutedSeries }]
                : []),
            ]}
          >
            <LineChart data={premiumChartData}>
              <CartesianGrid {...chart.grid} />
              <XAxis dataKey="dateLabel" {...chart.axis} />
              <YAxis yAxisId="left" {...chart.axis} />
              <YAxis
                yAxisId="right"
                orientation="right"
                {...chart.axis}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                {...chart.tooltip}
                formatter={(value: number, name: string) =>
                  name === "Premium %" ? [`${value}%`, name] : [value, name]
                }
              />
              <Legend {...chart.legend} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="totalPremiumUsers"
                name="Premium users"
                stroke={YELLOW}
                {...primaryLineProps}
              />
              {hasPrevPremium && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="prevTotalPremiumUsers"
                  name="Previous 24h"
                  stroke={chart.mutedSeries}
                  {...comparisonLineProps}
                />
              )}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="paidConversionRate"
                name="Premium %"
                stroke={ORANGE}
                {...rateLineProps}
              />
            </LineChart>
          </ChartWrapper>
          <ChartWrapper
            title="New IAP"
            description={hasPrevIap && !prevLoading ? COMPARISON_DESCRIPTION : undefined}
            averages={[
              {
                label: "IAP users",
                value: calcAvg(chartData, "totalIapUsers"),
                color: CYAN,
              },
              ...(hasPrevIap
                ? [{ label: "prev 24h", value: calcAvg(iapChartData, "prevTotalIapUsers"), color: chart.mutedSeries }]
                : []),
            ]}
          >
            <LineChart data={iapChartData}>
              <CartesianGrid {...chart.grid} />
              <XAxis dataKey="dateLabel" {...chart.axis} />
              <YAxis {...chart.axis} />
              <Tooltip {...chart.tooltip} />
              {hasPrevIap && <Legend {...chart.legend} />}
              <Line
                type="monotone"
                dataKey="totalIapUsers"
                name="IAP users"
                stroke={CYAN}
                {...primaryLineProps}
              />
              {hasPrevIap && (
                <Line
                  type="monotone"
                  dataKey="prevTotalIapUsers"
                  name="Previous 24h"
                  stroke={chart.mutedSeries}
                  {...comparisonLineProps}
                />
              )}
            </LineChart>
          </ChartWrapper>
          {renderSubscriptionChart(
            "Premium 1 week",
            "newPremium1Week",
            "renewalPremium1Week",
            PURPLE,
            PINK,
          )}
          {renderSubscriptionChart(
            "Premium 3 months",
            "newPremium3Months",
            "renewalPremium3Months",
            INDIGO,
            BLUE,
          )}
          {renderRevenueChart("Hot likes IAP", "totalHotLikesIap", RED)}
          {renderRevenueChart("Winks IAP", "totalWinksIap", PINK)}
          {renderRevenueChart("Unlock chat IAP", "totalUnlockChatIap", PURPLE)}
          {renderRevenueChart("Boost IAP", "totalBoostIap", ORANGE)}
          {renderRevenueChart("Spotlight IAP", "totalSpotlightIap", YELLOW)}
          {renderRevenueChart("100 votes IAP", "totalVotesIap", GREEN)}
          {renderRevenueChart("Live gift IAP", "totalLiveGiftIap", BLUE)}
          <ChartWrapper
            title="New finished onboarding"
            averages={[
              {
                label: "finished onboarding",
                value: calcAvg(chartData, "totalFinishedOnboarding"),
                color: BLUE,
              },
              {
                label: "completion %",
                value: calcAvg(chartData, "onboardingCompletionRate", true),
                color: CYAN,
              },
              {
                label: "premium %",
                value: calcAvg(chartData, "premiumOfOnboardingRate", true),
                color: YELLOW,
              },
            ]}
          >
            <LineChart data={chartData}>
              <CartesianGrid {...chart.grid} />
              <XAxis dataKey="dateLabel" {...chart.axis} />
              <YAxis yAxisId="left" {...chart.axis} />
              <YAxis
                yAxisId="right"
                orientation="right"
                {...chart.axis}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                {...chart.tooltip}
                formatter={(value: number, name: string) =>
                  name === "Completion %" || name === "Premium %"
                    ? [`${value}%`, name]
                    : [value, name]
                }
              />
              <Legend {...chart.legend} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="totalFinishedOnboarding"
                name="Finished onboarding"
                stroke={BLUE}
                {...primaryLineProps}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="onboardingCompletionRate"
                name="Completion %"
                stroke={CYAN}
                {...rateLineProps}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="premiumOfOnboardingRate"
                name="Premium %"
                stroke={YELLOW}
                {...rateLineProps}
              />
            </LineChart>
          </ChartWrapper>
          {renderCountAndRateChart(
            "Female finished onboarding",
            "femaleFinishedOnboarding",
            "Female finished",
            PINK,
            "femaleOnboardingRate",
            "Female %",
            PURPLE,
          )}
          {renderCountAndRateChart(
            "Photo finished onboarding",
            "photoFinishedOnboarding",
            "Photo finished",
            ORANGE,
            "photoOnboardingRate",
            "Photo %",
            YELLOW,
          )}
          {renderCountAndRateChart(
            "Push finished onboarding",
            "pushFinishedOnboarding",
            "Push finished",
            PURPLE,
            "pushOnboardingRate",
            "Push %",
            PINK,
          )}
          {renderSingleLineChart(
            "New premium onboarding",
            "totalPremiumOnboarding",
            CYAN,
          )}
          <ChartWrapper
            title="New push activated"
            description={hasPrevPush && !prevLoading ? COMPARISON_DESCRIPTION : undefined}
            averages={[
              { label: "push activated", value: calcAvg(chartData, "totalPushActivated"), color: LIME },
              ...(hasPrevPush
                ? [{ label: "prev 24h", value: calcAvg(pushChartData, "prevTotalPushActivated"), color: chart.mutedSeries }]
                : []),
            ]}
          >
            <LineChart data={pushChartData}>
              <CartesianGrid {...chart.grid} />
              <XAxis dataKey="dateLabel" {...chart.axis} />
              <YAxis {...chart.axis} />
              <Tooltip {...chart.tooltip} />
              {hasPrevPush && <Legend {...chart.legend} />}
              <Line
                type="monotone"
                dataKey="totalPushActivated"
                name="Push activated"
                stroke={LIME}
                {...primaryLineProps}
              />
              {hasPrevPush && (
                <Line
                  type="monotone"
                  dataKey="prevTotalPushActivated"
                  name="Previous 24h"
                  stroke={chart.mutedSeries}
                  {...comparisonLineProps}
                />
              )}
            </LineChart>
          </ChartWrapper>
          {renderSingleLineChart(
            "New rewarded ads watched",
            "totalRewardedAdsWatched",
            RED,
          )}
          {renderSingleLineChart(
            "Invite codes used",
            "inviteCodesUsed",
            ORANGE,
          )}
          {renderSingleLineChart(
            "Active invite codes",
            "totalActiveInviteCodes",
            PURPLE,
          )}

          <ChartWrapper
            title="Paid conversion rate"
            description="New premium / new users"
            averages={[
              {
                label: "premium / new users",
                value: calcAvg(chartData, "paidConversionRate", true),
                color: YELLOW,
              },
              {
                label: "premium / new finished onboarding",
                value: calcAvg(chartData, "premiumOfOnboardingRate", true),
                color: ORANGE,
              },
            ]}
          >
            <LineChart data={chartData}>
              <CartesianGrid {...chart.grid} />
              <XAxis dataKey="dateLabel" {...chart.axis} />
              <YAxis {...chart.axis} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                {...chart.tooltip}
                formatter={(value: number, name: string) => [
                  `${value}%`,
                  name,
                ]}
              />
              <Legend {...chart.legend} />
              <Line
                type="monotone"
                dataKey="paidConversionRate"
                name="Premium / new users"
                stroke={YELLOW}
                {...primaryLineProps}
              />
              <Line
                type="monotone"
                dataKey="premiumOfOnboardingRate"
                name="Premium / new finished onboarding"
                stroke={ORANGE}
                {...rateLineProps}
              />
            </LineChart>
          </ChartWrapper>

          <ChartWrapper
            title="Onboarding paywall conversion rate"
            description="New premium onboarding / new users"
            averages={[
              {
                label: "paywall conversion rate",
                value: calcAvg(chartData, "onboardingPaywallRate", true),
                color: PINK,
              },
            ]}
          >
            <LineChart data={chartData}>
              <CartesianGrid {...chart.grid} />
              <XAxis dataKey="dateLabel" {...chart.axis} />
              <YAxis {...chart.axis} tickFormatter={(v) => `${v}%`} />
              <Tooltip
                {...chart.tooltip}
                formatter={(value: number, name: string) => [
                  `${value}%`,
                  name,
                ]}
              />
              <Line
                type="monotone"
                dataKey="onboardingPaywallRate"
                name="Paywall conversion rate"
                stroke={PINK}
                {...primaryLineProps}
              />
            </LineChart>
          </ChartWrapper>

          {topSources.length > 0 && (
            <ChartWrapper
              title="Subscription sources over time"
              averages={[
                ...topSources.map((source, i) => ({
                  label: source,
                  value: calcAvg(subscriptionSourceData, source),
                  color: CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length],
                })),
                { label: "Other", value: calcAvg(subscriptionSourceData, "Other"), color: chart.mutedSeries },
              ]}
            >
              <LineChart data={subscriptionSourceData}>
                <CartesianGrid {...chart.grid} />
                <XAxis dataKey="dateLabel" {...chart.axis} />
                <YAxis {...chart.axis} />
                <Tooltip {...chart.tooltip} />
                <Legend {...chart.legend} />
                {topSources.map((source, i) => (
                  <Line
                    key={source}
                    type="monotone"
                    dataKey={source}
                    stroke={CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
                <Line
                  key="Other"
                  type="monotone"
                  dataKey="Other"
                  stroke={chart.mutedSeries}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 2 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ChartWrapper>
          )}
          {topVersions.length > 0 && (
            <ChartWrapper
              title="App versions over time"
              averages={[
                ...topVersions.map((version, i) => ({
                  label: version,
                  value: calcAvg(appVersionChartData, version),
                  color: CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length],
                })),
                { label: "Other", value: calcAvg(appVersionChartData, "Other"), color: chart.mutedSeries },
              ]}
            >
              <LineChart data={appVersionChartData}>
                <CartesianGrid {...chart.grid} />
                <XAxis dataKey="dateLabel" {...chart.axis} />
                <YAxis {...chart.axis} />
                <Tooltip {...chart.tooltip} />
                <Legend {...chart.legend} />
                {topVersions.map((version, i) => (
                  <Line
                    key={version}
                    type="monotone"
                    dataKey={version}
                    stroke={CHART_SERIES_COLORS[i % CHART_SERIES_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
                <Line
                  key="Other"
                  type="monotone"
                  dataKey="Other"
                  stroke={chart.mutedSeries}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 2 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ChartWrapper>
          )}
        </ChartGrid>
      ) : (
        data &&
        !error && (
          <EmptyState
            Icon={PresentationChartLineIcon}
            title="No onboarding data for this period"
            description="Try a longer date range or the other platform."
          />
        )
      )}
    </Page>
  );
};

export default OnboardingAnalytics;
